import { useHistory, useParams } from 'react-router-dom';
import { routes } from 'flow/types/routes';

import Page from './Page';
import SettingHeader from './SettingHeader';
import RouteLink from '../ui-components/RouteLink';

import useAppContext from 'flow/hooks/useAppContext';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';

import { TypedMap } from 'flow/types/TypedMap';
import { MobileSubscription } from 'flow/types/Extension';
import {
    getAllMainSimcards,
    getAllTwinSimcards,
    sortSimcardByIMSI,
    hasActiveMainSimCards,
    hasActiveTwinSimCards,
    hasAccessToEsimSettings,
} from 'flow/utils/sim-card-utils';
import { hasMobileLicence } from 'flow/selectors/extension-selectors';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getUserCountryCode } from 'flow/selectors/selectors';
import Divider from '../ui-components/Divider';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

interface Props {
    mobileSubscription: TypedMap<MobileSubscription>;
}

const MultiSimcard = ({ mobileSubscription }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { isSinglePaneMode } = useSinglePaneMode();

    const userHasMobileLicense = useAppSelector((state) =>
        hasMobileLicence(state, extensionKey),
    );
    const userCountryCode = useAppSelector((state) =>
        getUserCountryCode(state, extensionKey).toLowerCase(),
    );

    const allMainSimcards = getAllMainSimcards(mobileSubscription);
    const allTwinSimcards = getAllTwinSimcards(mobileSubscription);
    const hasAccessToEsim = hasAccessToEsimSettings(
        mobileSubscription,
        userHasMobileLicense,
        userCountryCode,
    );
    const hasActiveSimCardsResult = hasActiveMainSimCards(mobileSubscription);
    const hasActiveTwinSimCardsResult =
        hasActiveTwinSimCards(mobileSubscription);

    return (
        <Page
            header="settings.mobile"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            <SettingHeader>
                <div className="flex justify-between">
                    <p>{i18next.t('settings.simcard_for_voice_data')}</p>
                </div>
            </SettingHeader>

            {hasActiveSimCardsResult
                ? sortSimcardByIMSI(allMainSimcards).map(
                      (sim: any, index: any) => {
                          return (
                              <RouteLink
                                  key={sim.get('key')}
                                  to={generatePath(
                                      routes['settings.mobile.simKey'],
                                      {
                                          extensionKey,
                                          simKey: sim.get('key'),
                                      },
                                  )}
                              >
                                  {i18next.t('settings.main_simcard') +
                                      `${allMainSimcards.size > 1 ? ' ' + index + 1 : ''}`}
                              </RouteLink>
                          );
                      },
                  )
                : null}

            {!hasActiveSimCardsResult && hasAccessToEsim && (
                <RouteLink to={routes['settings.mobile.esim']}>
                    {i18next.t('settings.esim')}
                </RouteLink>
            )}

            {hasActiveTwinSimCardsResult ? (
                <>
                    <Divider className="my-3" />
                    <SettingHeader>
                        <div className="flex justify-between">
                            <p>{i18next.t('settings.simcard_for_data')}</p>
                        </div>
                    </SettingHeader>
                    {sortSimcardByIMSI(allTwinSimcards).map(
                        (sim: any, index: any) => {
                            return (
                                <RouteLink
                                    key={sim.get('key')}
                                    to={generatePath(
                                        routes['settings.mobile.simKey'],
                                        {
                                            extensionKey,
                                            simKey: sim.get('key'),
                                        },
                                    )}
                                >
                                    {i18next.t('settings.twin_simcard') +
                                        `${allTwinSimcards.size > 1 ? ' ' + index + 1 : ''}`}
                                </RouteLink>
                            );
                        },
                    )}
                </>
            ) : null}
        </Page>
    );
};

export default MultiSimcard;
